import type { AudioModel } from "@coffitivity/shared";
import { useAudio } from "@/app/providers/audio-provider";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn, getBaseName } from "@/lib/utils";
import { Pause, Play, Lock } from "lucide-react";
import { H2 } from "@/components/ui/h2";
import ResponsiveImage from "@/components/responsive-image";
import { TrackOrGroupLoadError } from "@/components/errors/trackOrGroupLoadError";
import { BlockSkeleton } from "./block-skeleton";
import { useApp } from "@/app/providers/app-provider";

export function TrackBlock({ track }: { track: AudioModel }) {
	const { handlePlay, currentTrack, audioStatus, handlePause } = useAudio();
	const { appStore } = useApp();

	const isPremium = track.isPremium && !appStore.isUserSubscribed;

	return (
		<Link key={track.id} to={`/library/track/${track.slug}`}>
			<div className="group flex flex-col">
				<div className="relative w-full pb-[100%] rounded-lg overflow-hidden shrink-0">
					<ResponsiveImage
						baseName={getBaseName(track.slug!)}
						sizes={["small", "medium"]}
						alt={track.name}
						className={cn(
							"absolute h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-110",
							isPremium && "opacity-50",
						)}
					/>
					<Button
						size="icon"
						variant="secondary"
						className={cn(
							"absolute inset-0 m-auto w-14 h-14 [&_svg]:size-6 rounded-full bg-primary/80 hover:bg-primary transition-colors",
							isPremium && "bg-slate-400/80 hover:bg-slate-400",
						)}
						onClick={(e) => {
							e.preventDefault(); // Prevent navigation
							audioStatus === "PLAYING" && currentTrack?.id === track.id
								? handlePause()
								: handlePlay(track);
						}}
					>
						{audioStatus === "PLAYING" && currentTrack?.id === track.id ? (
							<>
								<Pause fill="#000" className="text-white" strokeWidth={2} />
								<span className="sr-only">Pause</span>
							</>
						) : (
							<>
								<Play fill="#000" className="text-white" strokeWidth={2} />
								<span className="sr-only">Play</span>
							</>
						)}
					</Button>
				</div>

				<H2 className="p-0 pt-2 text-lg lg:text-lg border-0 truncate">
					{isPremium && <Lock size={18} className="inline-block mr-2 mb-1" />}
					{track.name}
				</H2>
			</div>
		</Link>
	);
}

export const TrackGrid = ({
	className,
	isError = true,
	isLoading = true,
	limit,
	tracks,
}: {
	className?: string;
	isError?: boolean;
	isLoading?: boolean;
	limit?: number;
	tracks: AudioModel[];
}) => {
	// Apply the limit if provided
	const displayedTracks = limit ? tracks.slice(0, limit) : tracks;

	// Create an array for skeleton placeholders
	const skeletonCount = limit || 10; // Default to 10 items if no limit is provided
	const skeletons = Array.from({ length: skeletonCount }, (_, i) => i);

	// If there's an error, show an error message
	if (isError) return <TrackOrGroupLoadError className="col-span-full" />;

	return (
		<div
			className={cn(
				"grid grid-cols-2 gap-4 gap-y-12 lg:grid-cols-4 xl:grid-cols-5",
				className,
			)}
		>
			{isLoading
				? skeletons.map((index) => <BlockSkeleton key={`skeleton-${index}`} />)
				: displayedTracks.map((track) => (
						<TrackBlock key={track.id} track={track} />
					))}
		</div>
	);
};
